import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Loader2, Search, TestTube } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { Campo, Seccion, entrada } from './ajustes';

interface Identidad {
  identificacion: string;
  tipo: string;
  nombre: string;
  nombreComercial?: string;
  direccion?: string;
  telefono?: string;
  correo?: string;
  estado?: string;
  fechaNacimiento?: string;
  estadoCivil?: string;
  condicion?: string;
  fallecido: boolean;
}

export function PruebaDeIdentidad() {
  const [identificacion, setIdentificacion] = useState('');

  const prueba = useMutation({
    mutationFn: (cual: string) => api.get<Identidad>(`/identidades/${cual.trim()}`),
  });

  const puedeProbar = /^[0-9]{10}([0-9]{3})?$/.test(identificacion.trim());

  return (
    <Seccion icono={TestTube} color="indigo" titulo="Probar la consulta">
      <form
        className="grid gap-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (puedeProbar) {
            prueba.mutate(identificacion);
          }
        }}
      >

        <div className="flex flex-wrap items-end gap-3">
          <span className="min-w-[16rem] flex-1">
            <Campo etiqueta="Cédula o RUC" icono={Search}>
              <input
                value={identificacion}
                onChange={(evento) => setIdentificacion(evento.target.value.replace(/\D/g, ''))}
                maxLength={13}
                inputMode="numeric"
                placeholder="0501234567"
                className={`${entrada} importe pl-10`}
              />
            </Campo>
          </span>

          <button
            type="submit"
            disabled={!puedeProbar || prueba.isPending}
            title="Consultar"
            aria-label="Consultar"
            className="grid size-11 shrink-0 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {prueba.isPending ? (
              <Loader2 className="size-5 animate-spin" aria-hidden />
            ) : (
              <Search className="size-5" aria-hidden />
            )}
          </button>
        </div>

        <p className="-mt-2 text-[11px] leading-snug text-armazon-500">
          Diez dígitos para una persona, trece para una empresa.
        </p>

        {prueba.isError && (
          <p
            role="alert"
            className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-xs uppercase text-red-900"
          >
            <AlertCircle className="mt-0.5 size-4 shrink-0 text-red-600" aria-hidden />
            {prueba.error instanceof ErrorDeApi
              ? prueba.error.message
              : 'No se pudo consultar la identidad'}
          </p>
        )}

        {prueba.isSuccess && prueba.data && <Ficha identidad={prueba.data} />}
      </form>
    </Seccion>
  );
}

function Ficha({ identidad }: { identidad: Identidad }) {
  return (
    <div className="rounded-xl border border-marca-200 bg-marca-50/40 p-4">
      <p className="flex items-center gap-2 text-xs font-bold uppercase text-marca-800">
        <CheckCircle2 className="size-4" aria-hidden />
        El servicio respondió · {identidad.tipo === 'empresa' ? 'Empresa' : 'Persona'}
      </p>

      {identidad.fallecido && (
        <p className="mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold uppercase text-red-800">
          <AlertCircle className="size-4 shrink-0" aria-hidden />
          Consta su defunción en el Registro Civil
        </p>
      )}

      <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
        <Dato rotulo="Nombre" valor={identidad.nombre} ancho />
        <Dato rotulo="Nombre comercial" valor={identidad.nombreComercial} />
        <Dato rotulo="Identificación" valor={identidad.identificacion} />
        <Dato rotulo="Dirección" valor={identidad.direccion} ancho />
        <Dato rotulo="Teléfono" valor={identidad.telefono} />
        <Dato rotulo="Correo" valor={identidad.correo} />
        <Dato rotulo="Fecha de nacimiento" valor={identidad.fechaNacimiento} />
        <Dato rotulo="Estado civil" valor={identidad.estadoCivil} />
        <Dato rotulo="Estado" valor={identidad.estado} />
        <Dato rotulo="Condición" valor={identidad.condicion} ancho />
      </dl>

      <p className="mt-3 text-[10px] uppercase leading-relaxed text-armazon-500">
        Es una comprobación: nada de esto se guarda en el sistema.
      </p>
    </div>
  );
}

function Dato({
  rotulo,
  valor,
  ancho,
}: {
  rotulo: string;
  valor: string | undefined;
  ancho?: boolean;
}) {
  if (valor == null || valor === '') {
    return null;
  }
  return (
    <div className={ancho ? 'sm:col-span-2' : ''}>
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
        {rotulo}
      </dt>
      <dd className="text-sm font-semibold uppercase text-armazon-800">{valor}</dd>
    </div>
  );
}
